import { AlertDialog, Badge, Button, EmptyState, Table, type BadgeTone, type TableColumn, type TableRowAction } from '@bennows/boows-react';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { formatDateTime } from '../../lib/format';
import { useToast } from '../../lib/toasts';
import type { OperatorMe } from '../auth/useOperatorMe';
import { NotFoundPage } from '../shell/NotFoundPage';
import { EditOperatorDialog } from './EditOperatorDialog';
import { operatorFailureKey } from './failureKey';
import { InviteOperatorDialog } from './InviteOperatorDialog';
import { useOperators, useOperatorWrites, type Operator } from './useOperators';

const STATUS_TONE: Record<Operator['status'], BadgeTone> = { active: 'success', invited: 'info', disabled: 'neutral' };

/** Below this width the list shows cards, like the web app's lists (DATA_TABLE_CARD_BREAKPOINT). */
const CARD_BREAKPOINT = 640;

type Confirm = { kind: 'disable' | 'reset'; operator: Operator } | null;

/** Operator accounts, owners only (REQ-140-002, `operators:manage`); without it nothing is requested. */
export function OperatorsPage() {
  const me = useOutletContext<OperatorMe>();
  return me.permissions.includes('operators:manage') ? <OperatorsManager me={me} /> : <NotFoundPage forbidden />;
}

function OperatorsManager({ me }: { readonly me: OperatorMe }) {
  const { t } = useTranslation();
  const toast = useToast();
  const operators = useOperators();
  const { update, resend } = useOperatorWrites();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Operator | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);

  const fail = (error: unknown, mailFailedKey = 'common.errorUnexpected') => toast(t(operatorFailureKey(error, mailFailedKey)), 'error');
  const write = (operator: Operator, changes: Parameters<typeof update.mutate>[0]['changes'], doneKey: string) =>
    update.mutate({ id: operator.id, changes }, { onSuccess: () => toast(t(doneKey, { name: operator.display_name })), onError: (error) => fail(error) });

  const columns: TableColumn<Operator>[] = [
    {
      key: 'name',
      header: t('operators.columns.name'),
      sortValue: (row) => row.display_name.toLowerCase(),
      cell: (row) => (
        <span className="ui-console-name">
          {row.display_name}
          {row.id === me.id ? <Badge variant="outline">{t('operators.you')}</Badge> : null}
        </span>
      ),
    },
    { key: 'email', header: t('operators.columns.email'), sortValue: (row) => row.email, cell: (row) => row.email },
    { key: 'role', header: t('operators.columns.role'), sortValue: (row) => row.role, cell: (row) => t(`roles.${row.role}`) },
    {
      key: 'status',
      header: t('operators.columns.status'),
      sortValue: (row) => row.status,
      cell: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`operators.status.${row.status}`)}</Badge>,
    },
    {
      key: 'secondFactor',
      header: t('operators.columns.secondFactor'),
      cell: (row) => t(row.totp_confirmed ? 'operators.secondFactor.set' : 'operators.secondFactor.missing'),
    },
    {
      key: 'lastSignIn',
      header: t('operators.columns.lastSignIn'),
      sortValue: (row) => row.last_sign_in_at ?? '',
      cell: (row) => (row.last_sign_in_at ? formatDateTime(row.last_sign_in_at) : t('operators.never')),
    },
  ];

  const rowActions = (row: Operator): TableRowAction<Operator>[] => {
    const actions: TableRowAction<Operator>[] = [{ id: 'edit', label: t('operators.edit'), onRun: setEditing }];
    if (row.status === 'invited') {
      actions.push({
        id: 'resend',
        label: t('operators.resend'),
        onRun: () =>
          resend.mutate(row.id, {
            onSuccess: () => toast(t('operators.resent', { email: row.email })),
            onError: (error) => fail(error, 'operators.resendFailed'),
          }),
      });
    }
    if (row.totp_confirmed && row.status !== 'disabled') actions.push({ id: 'reset', label: t('operators.resetSecondFactor'), onRun: () => setConfirm({ kind: 'reset', operator: row }) });
    if (row.id !== me.id) {
      actions.push(
        row.status === 'disabled'
          ? { id: 'enable', label: t('operators.enable'), onRun: () => write(row, { disabled: false }, 'operators.confirm.enabled') }
          : { id: 'disable', label: t('operators.disable'), variant: 'danger', onRun: () => setConfirm({ kind: 'disable', operator: row }) },
      );
    }
    return actions;
  };

  const runConfirm = () => {
    if (!confirm) return;
    const { kind, operator } = confirm;
    setConfirm(null);
    if (kind === 'disable') write(operator, { disabled: true }, 'operators.confirm.disabled');
    else write(operator, { reset_second_factor: true }, 'operators.confirm.reset');
  };

  return (
    <div className="ui-stack">
      <PageHeader
        title={t('operators.title')}
        description={t('operators.description')}
        actions={
          <Button iconPrefix={<UserPlus aria-hidden="true" />} onClick={() => setInviting(true)}>
            {t('operators.invite')}
          </Button>
        }
      />
      {operators.isError ? (
        <div className="ui-error-state" role="alert">
          <p className="ui-error-state__text">{t('operators.loadError')}</p>
          <Button variant="secondary" onClick={() => void operators.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <Table
          ariaLabel={t('operators.listLabel')}
          rows={operators.data?.items ?? []}
          columns={columns}
          getRowId={(row) => row.id}
          loading={operators.isPending}
          rowActions={rowActions}
          rowActionsLabel={t('operators.actions')}
          rowActionsAriaLabel={(row) => t('operators.rowActions', { name: row.display_name })}
          emptyState={<EmptyState title={t('operators.empty')} />}
          labels={{ empty: t('operators.empty') }}
          sortFirstDirection="asc"
          cardBreakpoint={CARD_BREAKPOINT}
          viewToggle={false}
          mobileViewToggle={false}
          renderCard={(row) => (
            <div className="ui-console-card">
              <span className="ui-console-name">
                <strong>{row.display_name}</strong>
                {row.id === me.id ? <Badge variant="outline">{t('operators.you')}</Badge> : null}
              </span>
              <span className="ui-console-muted">{row.email}</span>
              <span className="ui-console-name">
                {t(`roles.${row.role}`)}
                <Badge tone={STATUS_TONE[row.status]}>{t(`operators.status.${row.status}`)}</Badge>
              </span>
            </div>
          )}
        />
      )}
      <InviteOperatorDialog open={inviting} onClose={() => setInviting(false)} />
      {editing ? <EditOperatorDialog key={editing.id} operator={editing} onClose={() => setEditing(null)} /> : null}
      <AlertDialog
        open={confirm !== null}
        tone={confirm?.kind === 'disable' ? 'danger' : 'default'}
        title={confirm ? t(`operators.confirm.${confirm.kind}Title`, { name: confirm.operator.display_name }) : ''}
        description={confirm ? t(`operators.confirm.${confirm.kind}Text`) : undefined}
        confirmLabel={confirm ? t(`operators.confirm.${confirm.kind}Action`) : undefined}
        cancelLabel={t('common.cancel')}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirm}
      />
    </div>
  );
}
